import { useState } from "react";
import { toast } from "sonner";
import { Check, Lightbulb, Loader2, X } from "lucide-react";
import { suggestStyle } from "@/lib/assistantApi";
import { safeApiError } from "@/lib/safeErrorText";

function Chip({ children, testId }) {
  return <span data-testid={testId} className="rounded-full border border-white/10 bg-white/[0.05] px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-white/75">{children}</span>;
}

function Suggestion({ s, catalog, onUse, onDismiss }) {
  const structure = catalog.structures.find((x) => x.key === s.structure)?.label;
  return (
    <div data-testid="style-helper-result" className="mt-4 rounded-2xl border border-amber-300/30 bg-amber-500/[0.07] p-4">
      <p className="font-display text-[15px] font-semibold text-white">{s.genre_key === "custom" ? s.custom_style : s.genre_label}</p>
      {s.why ? <p data-testid="style-helper-why" className="mt-1 text-[13px] text-white/70">{s.why}</p> : null}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {s.title ? <Chip testId="style-helper-title">“{s.title}”</Chip> : null}
        {s.bpm ? <Chip testId="style-helper-bpm">{s.bpm} BPM</Chip> : null}
        {s.key ? <Chip testId="style-helper-key">{s.key}</Chip> : null}
        {structure ? <Chip testId="style-helper-structure">{structure}</Chip> : null}
        {s.vocal_style ? <Chip testId="style-helper-vocal">{s.vocal_style} vocal</Chip> : null}
        {s.instruments.map((i) => <Chip key={i}>{i}</Chip>)}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" data-testid="style-helper-apply" onClick={onUse} className="inline-flex items-center gap-1.5 rounded-full bg-amber-300 px-4 py-2 font-display text-[13px] font-semibold text-[#05070f] hover:bg-amber-200">
          <Check className="h-4 w-4" /> Use these settings
        </button>
        <button type="button" data-testid="style-helper-dismiss" onClick={onDismiss} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-[13px] text-white/70 hover:bg-white/[0.08]">
          <X className="h-4 w-4" /> Not this one
        </button>
      </div>
    </div>
  );
}

/** "Song idea → style": ChatGPT picks genre, tempo, key, structure and instruments from a sentence. */
export default function StyleHelper({ catalog, form, onChange }) {
  const [idea, setIdea] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (idea.trim().length < 3 || busy) return;
    setBusy(true);
    try { setResult(await suggestStyle({ idea, title: form.title || "", lyrics: form.lyrics || "" })); }
    catch (e) { toast.error("Couldn't suggest a style", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };
  const apply = () => {
    const s = result;
    const patch = { genre_key: s.genre_key, custom_style: s.custom_style || form.custom_style };
    if (s.bpm) patch.bpm = s.bpm;
    if (s.key) patch.key = s.key;
    if (s.instruments.length) patch.instruments = s.instruments;
    if (s.structure) patch.structure = s.structure;
    if (s.vocal_style) patch.vocal_style = s.vocal_style;
    if (!form.title.trim() && s.title) patch.title = s.title;
    onChange(patch);
    setResult(null);
    toast.success("Style applied", { description: "Genre, tempo, key and instruments are set — change anything you like." });
  };
  return (
    <section data-testid="style-helper" className="rounded-3xl border border-white/[0.08] bg-gradient-to-br from-amber-500/[0.06] to-transparent p-5">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-amber-200/90"><Lightbulb className="h-4 w-4" /> Song idea to style · ChatGPT</p>
      <p className="mt-1 text-[13px] text-white/60">Describe the song in a sentence — ChatGPT picks the genre, tempo, key and instruments for you.</p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input data-testid="style-helper-input" value={idea} onChange={(e) => setIdea(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} maxLength={1500}
          placeholder="e.g. A joyful Easter victory song with a big choir answering me"
          className="min-w-0 flex-1 rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-amber-300/60 focus:outline-none" />
        <button type="button" data-testid="style-helper-run" onClick={run} disabled={busy || idea.trim().length < 3}
          className="inline-flex items-center justify-center gap-2 rounded-2xl border border-amber-300/40 bg-amber-500/15 px-4 py-3 font-display text-[13px] font-semibold text-amber-50 hover:bg-amber-500/25 disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lightbulb className="h-4 w-4" />} {busy ? "Thinking…" : "Suggest a style"}
        </button>
      </div>
      {result ? <Suggestion s={result} catalog={catalog} onUse={apply} onDismiss={() => setResult(null)} /> : null}
    </section>
  );
}
